/**
 * BorderBeamPanel — simplified, bulletproof version
 *
 * Uses a plain requestAnimationFrame loop to rotate a conic-gradient
 * masked to show only the border ring. No color-mix(), no springs,
 * no complex conditions that can silently prevent animation.
 */
import { useId, useRef, useEffect, useCallback, useMemo } from 'react';

// ── Gradient ──────────────────────────────────────────────────────────────────
function buildGradient(beams, colors) {
  const c1 = colors?.[0] ?? '#4f7cff';
  const c2 = colors?.[1] ?? '#ff6b5e';

  if (beams === 2) {
    return [
      'transparent 0deg',
      `transparent 15deg`,
      `${c1}bb 30deg`,
      `${c1} 42deg`,
      `#ffffff 47deg`,
      `transparent 55deg`,
      'transparent 195deg',
      `${c2}bb 210deg`,
      `${c2} 222deg`,
      `#ffffff 227deg`,
      `transparent 235deg`,
      'transparent 360deg',
    ].join(', ');
  }

  return [
    'transparent 0deg',
    `transparent 15deg`,
    `${c1}bb 30deg`,
    `${c1} 42deg`,
    `#ffffff 47deg`,
    `transparent 55deg`,
    'transparent 360deg',
  ].join(', ');
}

// ── Component ─────────────────────────────────────────────────────────────────
export function BorderBeamPanel({
  children,
  beams = 2,
  colors,
  thickness = 2,
  idleSpeed = 42,     // deg/s
  hoverSpeed = 240,   // deg/s
  glow = true,
  radius = 16,
  style = {},
  className = '',
  ...props
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const cls = `mk-${uid}`;
  const rootRef = useRef(null);
  const angleRef = useRef(0);
  const speedRef = useRef(idleSpeed);
  const rafRef = useRef(null);

  const gradient = useMemo(() => buildGradient(beams, colors), [beams, colors]);

  // ── CSS injected per-instance ──────────────────────────────────────────────
  const css = `
.${cls} {
  position: relative;
  border-radius: ${radius}px;
  isolation: isolate;
}
.${cls} .bbp-ring {
  position: absolute;
  inset: 0;
  border-radius: ${radius}px;
  background: conic-gradient(from var(--bbp-angle, 0deg), ${gradient});
  padding: ${thickness}px;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.${cls} .bbp-glow {
  position: absolute;
  inset: 0;
  border-radius: ${radius}px;
  background: conic-gradient(from var(--bbp-angle, 0deg), ${gradient});
  filter: blur(16px);
  opacity: 0.25;
  pointer-events: none;
  z-index: -1;
}
.${cls} .bbp-content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
}
  `.trim();

  // ── Animation loop ─────────────────────────────────────────────────────────
  useEffect(() => {
    let last = 0;

    function frame(now) {
      if (last) {
        const dt = Math.min((now - last) / 1000, 0.05); // cap at 50ms
        angleRef.current = (angleRef.current + speedRef.current * dt) % 360;
        rootRef.current?.style.setProperty('--bbp-angle', `${angleRef.current.toFixed(2)}deg`);
      }
      last = now;
      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // run once on mount

  const onEnter = useCallback(() => { speedRef.current = hoverSpeed; }, [hoverSpeed]);
  const onLeave = useCallback(() => { speedRef.current = idleSpeed; }, [idleSpeed]);

  return (
    <div
      ref={rootRef}
      className={[cls, className].filter(Boolean).join(' ')}
      style={{ '--bbp-angle': '0deg', ...style }}
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      {...props}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      {glow && <div className="bbp-glow" aria-hidden="true" />}
      <div className="bbp-ring" aria-hidden="true" />
      <div className="bbp-content">
        {children}
      </div>
    </div>
  );
}

export default BorderBeamPanel;
